// Scenario-save DEV registry (FB-6) — pairs each spec with its GENERATED envelope, loaded via Vite's
// `import.meta.glob`. Browser + vitest only (glob is a Vite transform). This module is referenced
// ONLY from `import.meta.env.DEV` branches (main.ts `__qa`, the DEV panel Scenarios pane), so it
// folds to dead code + tree-shakes out of the prod bundle — the same strip path as ui/dev.ts. The
// `gh-pages.sh` marker grep PROVES that per deploy (Ph3) rather than trusting it (R2).

import type { SaveEnvelope } from '../persistence';
import { FIXTURE_SPECS } from './specs';

/** Stamped on the Scenarios pane's DOM node so the strip gate can grep for it in the bundle: it
 *  appears iff this module does. Belt to the `gh-pages.sh` fixture-name brace. */
export const FIXTURES_SENTINEL = '__KAMI_FIXTURES__';

// Eager glob: `{ './saves/<name>.json': { default: <parsed envelope> } }`. Static imports Vite can
// tree-shake with the module when nothing live references it.
const saves = import.meta.glob('./saves/*.json', { eager: true }) as Record<
  string,
  { default: SaveEnvelope }
>;

export interface FixtureEntry {
  readonly name: string;
  readonly blurb: string;
  /** The Scenarios-pane section header (FB-6 grouping) — mirrors the spec's `group`. */
  readonly group: string;
  /** DEV mechanical fixtures (R0–R7 rung starts) — loadable by name, hidden from the pane list. */
  readonly hidden: boolean;
  readonly env: SaveEnvelope;
}

/** The specs joined to their on-disk saves, in spec order. LAZY on purpose: a top-level
 *  `FIXTURE_SPECS.map(… throw …)` const is a module side effect Rollup can't prove pure, so it would
 *  pin this DEV-only registry (specs + the glob'd JSON) into the PROD bundle even though every caller
 *  sits behind `import.meta.env.DEV`. Computing inside a function keeps this module side-effect-free,
 *  so it dead-code-eliminates — proven by the verify-dev-strip gate (R2). A spec with no generated
 *  save throws (the registry↔disk parity the round-trip test also guards) — run `fixtures:regen`. */
export function getFixtures(): readonly FixtureEntry[] {
  return FIXTURE_SPECS.map((spec) => {
    const mod = saves[`./saves/${spec.name}.json`];
    if (!mod)
      throw new Error(
        `fixture save missing for spec "${spec.name}" — run \`pnpm run fixtures:regen\``,
      );
    return {
      name: spec.name,
      blurb: spec.blurb,
      group: spec.group,
      hidden: spec.hidden ?? false,
      env: mod.default,
    };
  });
}

export function getFixture(name: string): FixtureEntry | undefined {
  return getFixtures().find((f) => f.name === name);
}
